import { useEffect, useState } from "react";
import { KeyRound, Play, Ban, History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const EVENT_IT = { "sms_2ck.legacy_read_authorized": "Autorizzazione", "sms_2ck.legacy_read_used": "Lettura eseguita", "sms_2ck.legacy_read_revoked": "Revoca", "sms_2ck.legacy_read_expired": "Scadenza automatica" };
const STATUS_IT = { active: "attiva", used: "consumata", revoked: "revocata", expired: "scaduta" };
const OUTCOME_COLOR = { OK: "text-[#10B981]", ERR: "text-[#EF4444]", UNCERTAIN: "text-orange-400" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT") : "—");
const QUALITY_IT = { HQD: "HQD (alta qualità, mittente dinamico)", HQ: "HQ (alta qualità)", LQ: "LQ (bassa qualità)", MQ: "MQ (media qualità)" };

function LastResult({ r, by }) {
  const byQ = r.credits_by_quality || {};
  const classes = Object.keys(byQ);
  return (
    <div className="space-y-1 rounded-md border border-[#1F2937] p-2" data-testid="sms-2ck-legacy-last-result">
      <p>Ultima verifica <b data-testid="sms-2ck-legacy-last-read-at">{fmt(r.read_at)}</b> da {by}: <b className={OUTCOME_COLOR[r.outcome]}>{r.outcome}</b> · {r.code}{r.http != null && ` · HTTP ${r.http}`}{r.format && ` · formato ${r.format}`}{r.incomplete && <b className="text-orange-400" data-testid="sms-2ck-legacy-partial-flag"> · RISPOSTA INCOMPLETA: dati parziali, nessun saldo definitivo</b>}</p>
      {r.credits != null && (
        <p data-testid="sms-2ck-legacy-credit-available">{r.incomplete ? "Valore estratto da risposta PARZIALE (non è un saldo definitivo)" : "Riferimento provvisorio"}{r.primary_quality ? ` — classe ${r.primary_quality}` : ""}: <b>{byQ[r.primary_quality]?.credit ?? r.credits}</b> · unità di misura: <b data-testid="sms-2ck-legacy-credit-unit">{r.unit || "non dichiarata"}</b>{!r.unit_confirmed && <span className="text-amber-400"> (da confermare con il provider — nessuna conversione in euro né stima SMS)</span>}</p>
      )}
      {r.credits != null && <p className="text-[11px] text-muted-foreground" data-testid="sms-2ck-legacy-credit-disclaimer">Il valore della classe {r.primary_quality || "prioritaria"} è solo un riferimento: non va considerato automaticamente il credito disponibile per gli invii HIGH.</p>}
      {classes.length > 1 && (
        <ul className="text-[11px] text-muted-foreground" data-testid="sms-2ck-legacy-credit-by-quality">
          {classes.map((q) => <li key={q}>{QUALITY_IT[q] || q}: {byQ[q].credit}{byQ[q].type ? ` ${byQ[q].type}` : ""}{byQ[q].type == null && r.incomplete ? " (type mancante nella risposta parziale)" : ""}</li>)}
          <li className="italic">I saldi per classe restano separati e non vengono sommati.</li>
        </ul>
      )}
      {r.raw && <p className="text-muted-foreground break-all">risposta: “{r.raw}”</p>}
    </div>
  );
}

function AuthorizeForm({ maxTtl, defaultTtl, onDone }) {
  const [f, setF] = useState({ checked: false, note: "", ttl: defaultTtl });
  const [busy, setBusy] = useState(false);
  const valid = f.checked && f.note.trim().length >= 10 && f.ttl >= 1 && f.ttl <= maxTtl;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/sms/2ck/legacy-read/authorize", { confirm: true, note: f.note, ttl_minutes: Number(f.ttl) });
      toast.success("Autorizzazione creata: nessuna chiamata a 2CK eseguita. Premi «Esegui 1 lettura» entro la scadenza.");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="sms-2ck-legacy-authorize-form">
      <div className="grid sm:grid-cols-[1fr_8rem] gap-2">
        <div className="space-y-1">
          <Label className="text-[11px]">Nota obbligatoria (min 10 caratteri: motivo della lettura)</Label>
          <Textarea className="text-xs min-h-[50px]" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="sms-2ck-legacy-authorize-note" />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Validità (min, max {maxTtl})</Label>
          <Input type="number" min={1} max={maxTtl} className="text-xs" value={f.ttl} onChange={(e) => setF({ ...f, ttl: e.target.value })} data-testid="sms-2ck-legacy-authorize-ttl" />
        </div>
      </div>
      <label className="flex items-start gap-2 text-xs cursor-pointer">
        <input type="checkbox" className="mt-0.5" checked={f.checked} onChange={(e) => setF({ ...f, checked: e.target.checked })} data-testid="sms-2ck-legacy-authorize-checkbox" />
        <span>Autorizzo UNA sola lettura del credito via API legacy (get-credit) per questo canale. L'autorizzazione non esegue alcuna chiamata.</span>
      </label>
      <Button size="sm" onClick={submit} disabled={!valid || busy} data-testid="sms-2ck-legacy-authorize-submit"><KeyRound className="h-4 w-4 mr-1" /> Autorizza 1 lettura</Button>
    </div>
  );
}

function ActiveBox({ a, onDone }) {
  const [left, setLeft] = useState(a.seconds_left);
  const [reason, setReason] = useState("");
  const [revoking, setRevoking] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setLeft(a.seconds_left); const t = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000); return () => clearInterval(t); }, [a.id, a.seconds_left]);
  useEffect(() => { if (left === 0) onDone(); }, [left]); // eslint-disable-line react-hooks/exhaustive-deps
  const execute = async () => {
    if (!window.confirm("Eseguire ORA l'unica lettura del credito 2CK consentita? L'autorizzazione verrà consumata anche in caso di errore o timeout (nessun retry).")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/sms/2ck/legacy-read/execute", { confirm: true, authorization_id: a.id });
      const r = data.result;
      (r.outcome === "OK" ? toast.success : toast.error)(`Lettura eseguita: ${r.outcome} · ${r.code}${r.credits != null ? ` · credito ${r.credits}` : ""}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e)); onDone(); } finally { setBusy(false); }
  };
  const revoke = async () => {
    try {
      await api.post("/sms/2ck/legacy-read/revoke", { confirm: true, reason });
      toast.success("Autorizzazione revocata (audit registrato)"); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  return (
    <div className="space-y-2 rounded-md border border-sky-500/40 bg-sky-500/10 p-2" data-testid="sms-2ck-legacy-active">
      <p>Autorizzazione <b>attiva</b> · creata da <b>{a.created_by}</b> il {fmt(a.created_at)} · scade tra <b data-testid="sms-2ck-legacy-countdown">{Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</b> · chiamate residue <b>{a.remaining_calls}</b>/{a.max_calls}</p>
      <p className="text-muted-foreground">Nota: “{a.note}”</p>
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" onClick={execute} disabled={busy} data-testid="sms-2ck-legacy-execute-btn"><Play className="h-4 w-4 mr-1" /> Esegui 1 lettura (get-credit)</Button>
        {!revoking && <Button size="sm" variant="ghost" className="text-[#EF4444]" onClick={() => setRevoking(true)} data-testid="sms-2ck-legacy-revoke-open"><Ban className="h-4 w-4 mr-1" /> Revoca</Button>}
      </div>
      {revoking && (
        <div className="space-y-1 pt-1 border-t border-[#1F2937]" data-testid="sms-2ck-legacy-revoke-form">
          <Textarea className="text-xs min-h-[40px]" placeholder="Motivazione (min 10 caratteri)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="sms-2ck-legacy-revoke-reason" />
          <div className="flex gap-2"><Button size="sm" variant="destructive" disabled={reason.trim().length < 10} onClick={revoke} data-testid="sms-2ck-legacy-revoke-submit">Conferma revoca</Button><Button size="sm" variant="ghost" onClick={() => setRevoking(false)}>Annulla</Button></div>
        </div>
      )}
    </div>
  );
}

export function Sms2ckLegacyCreditOneShot({ isSuperAdmin }) {
  const [d, setD] = useState(null);
  const load = () => api.get("/sms/2ck/legacy-read").then((r) => setD(r.data)).catch((e) => setD({ error: formatApiErrorDetail(e) }));
  useEffect(() => { if (isSuperAdmin) load(); }, [isSuperAdmin]);
  if (!isSuperAdmin || !d) return null;
  if (d.error) return <p className="text-[11px] text-amber-500" data-testid="sms-2ck-legacy-error">{d.error}</p>;
  const last = d.history.find((h) => h.result);
  return (
    <div className="text-xs rounded-lg border border-[#1F2937] p-3 space-y-2" data-testid="sms-2ck-legacy-oneshot">
      <p className="font-semibold flex items-center gap-1"><KeyRound className="h-4 w-4" /> Lettura one-shot credito (API legacy, solo Super Admin)</p>
      <p className="text-muted-foreground">Il flag globale SMS_2CK_LEGACY_READ_ENABLED resta <b>{d.legacy_read_enabled ? "true" : "false"}</b>: il pulsante ↻ e i controlli automatici non leggono il credito. Solo questa sezione può eseguire UNA chiamata get-credit, previa autorizzazione tracciata. Chiave: {d.key_source === "legacy" ? "legacy distinta" : d.key_source === "v1" ? "API Key v1" : "non configurata"}.</p>
      {d.active ? <ActiveBox a={d.active} onDone={load} /> : <AuthorizeForm maxTtl={d.max_ttl_minutes} defaultTtl={d.default_ttl_minutes} onDone={load} />}
      {last && <LastResult r={last.result} by={last.used_by} />}
      {d.audit.length > 0 && (
        <details className="text-[11px]" data-testid="sms-2ck-legacy-audit">
          <summary className="cursor-pointer text-slate-400 flex items-center gap-1"><History className="h-3 w-3" /> Storico autorizzazioni ({d.history.length}) e audit ({d.audit.length})</summary>
          <ul className="mt-1 space-y-1">
            {d.audit.map((r) => (
              <li key={r.id} className="text-muted-foreground" data-testid={`sms-2ck-legacy-audit-${r.id}`}>
                {fmt(r.ts)} · <b>{EVENT_IT[r.event] || r.event}</b> · {r.user_email || "sistema"}{r.user_role ? ` (${r.user_role})` : ""} · {STATUS_IT[r.previous_status] || "—"} → {STATUS_IT[r.new_status]}
                {r.note && <> · nota: “{r.note}”</>}{r.reason && <> · motivo: “{r.reason}”</>}{r.outcome && <> · esito: <b className={OUTCOME_COLOR[r.outcome]}>{r.outcome}</b> {r.code}{r.http != null && ` (HTTP ${r.http})`}{r.credits != null && `, credito ${r.credits}`}</>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
